import React, { FC, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { Column, Container, Row } from '../../../../../../components/grid/Grid';
import {
  ModalFullScreen,
  ModalFullScreenHeader,
  ModalFullScreenContent,
} from '../../../../../../components/modal';
import { VersionsSelector } from '../versions-selector/VersionsSelector';
import { VersionsDiff } from '../versions-diff/VersionsDiff';
import { MaterialIcon } from '../../../../../../components/material-icon/MaterialIcon';

import { doctrineAnalytics } from '../../../../../../utils/analyticsUtils';

import { LegislationInfos } from '../../../../legislation.types';

import styles from './compare_versions_modal.module.scss';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  articleId: string;
  legislationInfosAll: LegislationInfos[];
  defaultContent: string;
  articleTitle: string;
};

export const CompareVersionsModal: FC<Props> = ({
  isOpen,
  onRequestClose,
  legislationInfosAll,
  articleId,
  defaultContent,
  articleTitle,
}) => {
  const [comparedArticleVersionId, setComparedArticleVersionId] = useState<
    string
  >('');
  const [baseArticleVersionId, setBaseArticleVersionId] = useState<string>(
    articleId,
  );
  const [articleVersionToCompare, setArticleVersionToCompare] = useState<
    LegislationInfos[]
  >([]);

  const fireAnalyticEvent = () => {
    const indexCompare = legislationInfosAll.findIndex(
      (el: { articleId: string }) => el.articleId === comparedArticleVersionId,
    );
    const indexPage = legislationInfosAll.findIndex(
      (el: { articleId: string }) => el.articleId === baseArticleVersionId,
    );
    doctrineAnalytics('Law CompareVersionDisplayed', {
      articleIdPage: baseArticleVersionId,
      indexPage,
      articleIdCompare: comparedArticleVersionId,
      indexCompare,
      reportOriginalQueryKey: true,
    });
  };

  const handleOnChangeComparedVersion = (
    newComparedArticleVersionId: string,
  ) => {
    setComparedArticleVersionId(newComparedArticleVersionId);
    fireAnalyticEvent();
  };

  const handleOnChangeBaseVersion = (newBaseArticleVersionId: string) => {
    const newAvailableVersionsToCompare = legislationInfosAll.filter(
      (version) => version.articleId !== newBaseArticleVersionId,
    );
    setBaseArticleVersionId(newBaseArticleVersionId);
    setArticleVersionToCompare(newAvailableVersionsToCompare);
    setComparedArticleVersionId(newAvailableVersionsToCompare[0].articleId);
    fireAnalyticEvent();
  };

  useEffect(() => {
    const availableVersionsToCompare = legislationInfosAll.filter(
      (version) => version.articleId !== articleId,
    );
    setArticleVersionToCompare(availableVersionsToCompare);
    setComparedArticleVersionId(availableVersionsToCompare[0].articleId);
  }, [articleId, legislationInfosAll]);

  const findComparedText = (id: string) => {
    const oldVersion = legislationInfosAll.find(
      (legislation: { articleId: string }) => legislation.articleId === id,
    );
    if (oldVersion) {
      return oldVersion.rawContent;
    }
    return defaultContent;
  };

  return (
    <ModalFullScreen
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      data-test-id="compare-versions-modal"
    >
      <ModalFullScreenHeader
        onClose={onRequestClose}
        data-test-id="compare-versions-modal-close"
      >
        {articleTitle}
      </ModalFullScreenHeader>
      <ModalFullScreenContent>
        <Container className={styles.contentContainer}>
          <Row>
            <Column>
              <h2 className={styles.contentTitle}>
                <FormattedMessage id="sections/versions/Versions.tsx/1" />
              </h2>
            </Column>
          </Row>
          <Row>
            <Column medium={7}>
              <VersionsSelector
                compareVersions={handleOnChangeBaseVersion}
                articleId={baseArticleVersionId}
                legislationInfosAll={legislationInfosAll}
              />
            </Column>
          </Row>
          <Row>
            <Column medium={6} className={styles.comparedInputRow}>
              <MaterialIcon
                name="compare_arrows"
                className={styles.compareIcon}
              />
              <VersionsSelector
                legislationInfosAll={articleVersionToCompare}
                compareVersions={handleOnChangeComparedVersion}
                articleId={comparedArticleVersionId}
              />
            </Column>
          </Row>
          <Row>
            <Column medium={8} className={styles.versionsDiffRow}>
              <VersionsDiff
                oldText={findComparedText(comparedArticleVersionId)}
                newText={findComparedText(baseArticleVersionId)}
              />
            </Column>
          </Row>
        </Container>
      </ModalFullScreenContent>
    </ModalFullScreen>
  );
};
